import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import TeamMemberEntry from '../components/TeamMemberEntry/TeamMemberEntry';
import ChipFilter from '../components/ChipFilter/ChipFilter';
import ContextMenu from '../components/ContextMenu';
import Tooltip from '../components/Tooltip';
import './TableView.scss';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';

function groupSkillsBySkillGroup(skills, skillGroups) {
  const groupMap = {};
  skillGroups.forEach(g => {
    groupMap[g.id] = { ...g, skills: [] };
  });
  skills.forEach(skill => {
    if (skill.skill_group_id && groupMap[skill.skill_group_id]) {
      groupMap[skill.skill_group_id].skills.push(skill);
    } else {
      // Ungrouped
      if (!groupMap['ungrouped']) groupMap['ungrouped'] = { id: 'ungrouped', title: 'Ungrouped', skills: [] };
      groupMap['ungrouped'].skills.push(skill);
    }
  });
  
  // Get groups with skills and sort them by the order property
  const groupsWithSkills = Object.values(groupMap).filter(g => g.skills.length > 0);
  
  // Sort by order property (if it exists)
  return groupsWithSkills.sort((a, b) => {
    // Handle ungrouped specially - always at the end
    if (a.id === 'ungrouped') return 1;
    if (b.id === 'ungrouped') return -1;
    
    // Sort by order if both have it
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }
    
    // If only one has order, prioritize the one with order
    if (a.order !== undefined) return -1;
    if (b.order !== undefined) return 1;
    
    // Default to id comparison if neither has order
    return a.id - b.id;
  });
}

// Droppable area component
function DroppableArea({ id, children, isOver, isValidDropTarget }) {
  const { setNodeRef } = useDroppable({ 
    id,
    disabled: !isValidDropTarget
  });
  
  return (
    <div
      ref={setNodeRef}
      className={`droppable-area ${isOver && isValidDropTarget ? 'droppable-hover' : ''}`}
    >
      {children}
    </div>
  );
}

function TableView({ skills, skillGroups, levels, teamMembers, onDataChange }) {
  // State: selected team members per cell: { [levelId-skillId]: [memberIds] }
  const [cellSelections, setCellSelections] = useState({});
  // Track user-initiated moves separately to preserve them across filter changes
  const [userMoves, setUserMoves] = useState([]);
  const [loadingCell, setLoadingCell] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [descriptions, setDescriptions] = useState([]);
  const [hoveredDesc, setHoveredDesc] = useState(null); // { key, x, y, text }
  const [selectedMembers, setSelectedMembers] = useState(['all']);
  const [contextMenu, setContextMenu] = useState(null);
  // Track member statuses locally: { [memberId-skillId]: status }
  const [memberStatuses, setMemberStatuses] = useState({});
  // Force UI updates when statuses change
  const [statusUpdateCounter, setStatusUpdateCounter] = useState(0);
  // Track active drag
  const [activeDrag, setActiveDrag] = useState(null);
  // Track which container is being hovered over
  const [activeDroppable, setActiveDroppable] = useState(null);
  // Local state for unassigned members by skill
  const [unassignedMembers, setUnassignedMembers] = useState({});

  const grouped = groupSkillsBySkillGroup(skills, skillGroups);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  // Initialize member statuses from teamMembers
  useEffect(() => {
    const statuses = {};
    teamMembers.forEach(member => {
      if (Array.isArray(member.skills)) {
        member.skills.forEach(skill => {
          if (skill.status) {
            statuses[`${member.id}-${skill.skill_id}`] = skill.status;
          }
        });
      }
    });
    setMemberStatuses(statuses);
  }, [teamMembers]);

  // Chip toggle logic
  const handleChipClick = (id) => {
    if (id === 'all') {
      setSelectedMembers(['all']);
    } else {
      if (selectedMembers.includes('all')) {
        setSelectedMembers([id]);
      } else if (selectedMembers.includes(id)) {
        const next = selectedMembers.filter(m => m !== id);
        setSelectedMembers(next.length === 0 ? ['all'] : next);
      } else {
        setSelectedMembers([...selectedMembers, id]);
      }
    }
  };

  // Filtered team members for display and dropdowns
  const displayTeamMembers =
    selectedMembers.includes('all') || selectedMembers.length === 0
      ? teamMembers
      : teamMembers.filter(m => selectedMembers.includes(String(m.id)));

  // Check if chip filter is applied (not showing "All")
  const isFiltered = !selectedMembers.includes('all');
  
  // Get the class name for filtered count
  const getFilteredCountClass = () => {
    if (!isFiltered) return '';
    return `filtered-count-${selectedMembers.length}`;
  };

  // Get unassigned members for a skill
  const getUnassignedMembersForSkill = useCallback((skillId, members, filtered) => {
    // Get all members assigned to this skill
    const assignedMemberIds = new Set();
    members.forEach(member => {
      if (Array.isArray(member.skills)) {
        member.skills.forEach(skill => {
          if (skill.skill_id === skillId) {
            assignedMemberIds.add(String(member.id));
          }
        });
      }
    });
    
    // Return all filtered members that are not assigned to this skill
    return filtered.filter(member => !assignedMemberIds.has(String(member.id)));
  }, []);

  // Load assignments on initial mount and when base data changes
  useEffect(() => {
    // Start with base data from teamMembers
    const selections = {};
    const assignedMembers = new Set(); // Track which members are assigned to skills
    
    teamMembers.forEach(member => {
      if (Array.isArray(member.skills)) {
        member.skills.forEach(assignment => {
          const cellKey = `${assignment.level_id}-${assignment.skill_id}`;
          if (!selections[cellKey]) selections[cellKey] = [];
          selections[cellKey].push(String(member.id));
          assignedMembers.add(`${member.id}-${assignment.skill_id}`);
        });
      }
    });
    
    // Apply user moves on top of the base data
    userMoves.forEach(move => {
      const memberSkillKey = `${move.memberId}-${move.skillId}`;
      
      // If this is the first move for a previously unassigned member, nothing to remove
      if (move.sourceKey && (assignedMembers.has(memberSkillKey) || move.initialMove === false)) {
        // Remove from source
        if (selections[move.sourceKey]) {
          selections[move.sourceKey] = selections[move.sourceKey].filter(id => id !== move.memberId);
        }
      }
      
      // Add to target if not null (null means member was removed from skill)
      if (move.targetKey) {
        if (!selections[move.targetKey]) selections[move.targetKey] = [];
        // Only add if not already there
        if (!selections[move.targetKey].includes(move.memberId)) {
          selections[move.targetKey].push(move.memberId);
        }
        assignedMembers.add(memberSkillKey);
      } else {
        // If targetKey is null, member was removed from skill
        assignedMembers.delete(memberSkillKey);
      }
    });
    
    setCellSelections(selections);
  }, [teamMembers, skills, levels, userMoves]);
  
  // Calculate unassigned members when filters or cell selections change
  useEffect(() => {
    const unassigned = {};
    const currentFiltered = selectedMembers.includes('all') || selectedMembers.length === 0
      ? teamMembers
      : teamMembers.filter(m => selectedMembers.includes(String(m.id)));
    
    // For each skill, find members that aren't assigned to any level
    skills.forEach(skill => {
      // Get all members assigned to this skill at any level
      const assignedMemberIds = new Set();
      Object.entries(cellSelections).forEach(([cellKey, memberIds]) => {
        // Cell key format is "levelId-skillId"
        const [levelId, skillId] = cellKey.split('-');
        if (parseInt(skillId) === skill.id) {
          memberIds.forEach(id => assignedMemberIds.add(id));
        }
      });
      
      // Filter out members who are already assigned to this skill
      unassigned[skill.id] = currentFiltered.filter(member => 
        !assignedMemberIds.has(String(member.id))
      );
    });
    
    setUnassignedMembers(unassigned);
  }, [teamMembers, skills, cellSelections, selectedMembers]);

  useEffect(() => {
    async function fetchDescriptions() {
      try {
        const res = await api.getSkillLevelDescriptions();
        setDescriptions(res.data);
      } catch {
        setDescriptions([]);
      }
    }
    fetchDescriptions();
  }, [skills, levels]);

  const getDescription = (skillId, levelId) => {
    return descriptions.find(d => d.skill_id === skillId && d.level_id === levelId);
  };

  const handleRemoveMember = async (levelId, skillId, memberId) => {
    const cellKey = `${levelId}-${skillId}`;
    setLoadingCell(cellKey + '-' + memberId);
    try {
      await api.removeSkillFromMember(memberId, skillId);
      
      // Record this move in userMoves
      setUserMoves(prev => {
        // Find the latest move for this member-skill
        const memberMoves = prev.filter(move => 
          move.memberId === memberId && move.skillId === skillId
        );
        
        // Remove all previous moves for this member and skill
        const otherMoves = prev.filter(move => 
          move.memberId !== memberId || move.skillId !== skillId
        );
        
        // Add this move (source but no target)
        return [
          ...otherMoves,
          {
            memberId,
            skillId,
            sourceKey: `${levelId}-${skillId}`,
            targetKey: null, // No target means removed
            timestamp: Date.now(),
            initialMove: memberMoves.length === 0
          }
        ];
      });
      
      // Notify parent about data change so other views can update
      if (onDataChange) {
        onDataChange();
      }
    } catch (e) {
      // Optionally show error
    } finally {
      setLoadingCell(null);
    }
  };

  const getMemberStatus = useCallback((memberId, skillId) => {
    // First check our local state
    const localStatus = memberStatuses[`${memberId}-${skillId}`];
    if (localStatus !== undefined) return localStatus;
    
    // Fall back to the data from props
    const member = teamMembers.find(m => String(m.id) === String(memberId));
    if (!member) return null;
    
    const skill = member.skills?.find(s => s.skill_id === skillId);
    return skill?.status || null;
  }, [memberStatuses, teamMembers, statusUpdateCounter]);

  const handleContextMenu = useCallback((e, memberId, skillId) => {
    // Get the member
    const member = teamMembers.find(m => String(m.id) === String(memberId));
    if (!member) return;
    
    // Allow if admin or if this is the user's own entry
    const isUserEntry = member.email === user?.email;
    if (!isAdmin && !isUserEntry) return;
    
    e.preventDefault();
    
    // Get current status using our helper function
    const status = getMemberStatus(memberId, skillId);
    
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      memberId,
      skillId,
      status: status || 'none'
    });
  }, [isAdmin, teamMembers, getMemberStatus, user?.email]);

  const handleUpdateStatus = useCallback(async (memberId, skillId, status) => {
    try {
      await api.updateMemberSkillStatus(memberId, skillId, status);
      
      // Update local state to reflect the change immediately
      setMemberStatuses(prev => ({
        ...prev,
        [`${memberId}-${skillId}`]: status === 'none' ? null : status
      }));
      
      // Update the contextMenu state to reflect the new status
      if (contextMenu && contextMenu.memberId === memberId && contextMenu.skillId === skillId) {
        setContextMenu(prev => ({
          ...prev,
          status: status === 'none' ? 'none' : status
        }));
      }
      
      // Force a re-render to update the UI
      setStatusUpdateCounter(prev => prev + 1);
      
      // Notify parent about data change so other views can update
      if (onDataChange) {
        onDataChange();
      }
      
    } catch (error) {
      console.error('Error updating status:', error);
      
      // Show a message for the "watched" status permission error
      if (error.response && error.response.data && error.response.data.error) {
        if (error.response.status === 403) {
          alert(error.response.data.error);
        }
      }
    }
  }, [contextMenu, onDataChange]);

  // Check if a drop target is valid (same skill column)
  const isValidDropTarget = useCallback((draggedId, targetId) => {
    if (!draggedId || !targetId) return false;
    
    // Parse IDs to get skill information
    const draggedMatch = draggedId.match(/(\d+)-(\d+)-(.+)/);
    const targetMatch = targetId.match(/droppable-(\d+)-(.+)/);
    
    if (!draggedMatch || !targetMatch) return false;
    
    // Check if skills match
    const draggedSkillId = draggedMatch[2];
    const targetSkillId = targetMatch[1];
    
    return draggedSkillId === targetSkillId;
  }, []);

  // Handle drag over - update active droppable
  const handleDragOver = useCallback((event) => {
    const { active, over } = event;
    setActiveDroppable(over ? over.id : null);
  }, []);

  // Handle drag end - update member's skill level
  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    setActiveDrag(null);
    setActiveDroppable(null);
    
    if (!active || !over) return;
    
    // Parse the IDs to get member, skill, and level info
    const [memberId, skillId, sourceLevelId] = active.id.split('-');
    
    // Get the member
    const member = teamMembers.find(m => String(m.id) === memberId);
    if (!member) return;
    
    // Check if user has permission (admin or own entry)
    const isUserEntry = member.email === user?.email;
    if (!isAdmin && !isUserEntry) return;
    
    // Parse the droppable ID to get target skill and level
    const droppableMatch = over.id.match(/droppable-(\d+)-(.+)/);
    if (!droppableMatch) return;
    
    const [_, targetSkillId, targetLevelId] = droppableMatch;
    
    // Ensure we're dropping in the same skill column
    if (skillId !== targetSkillId) return;
    
    // If dropping in the same level, do nothing
    if (sourceLevelId === targetLevelId) return;
    
    // Check if this is an initial assignment or a move from another level
    const isInitialAssignment = sourceLevelId === 'unassigned';
    
    // If target is 'unassigned', remove the skill from the member
    if (targetLevelId === 'unassigned') {
      try {
        await api.removeSkillFromMember(memberId, skillId);
        
        // Record this move in userMoves (removing the skill)
        setUserMoves(prev => {
          // Find the latest move for this member-skill
          const memberMoves = prev.filter(move => 
            move.memberId === memberId && move.skillId === skillId
          );
          
          // Remove all previous moves for this member and skill
          const otherMoves = prev.filter(move => 
            move.memberId !== memberId || move.skillId !== skillId
          );
          
          // Add this move (source but no target)
          return [
            ...otherMoves,
            {
              memberId,
              skillId,
              sourceKey: `${sourceLevelId}-${skillId}`,
              targetKey: null, // No target means removed
              timestamp: Date.now(),
              initialMove: memberMoves.length === 0
            }
          ];
        });
      } catch (error) {
        console.error('Error removing skill from member:', error);
      }
      return;
    }
    
    // Otherwise, update the member's skill level
    try {
      // Get current status
      const status = getMemberStatus(memberId, skillId);
      
      // Update the skill level
      await api.assignSkillToMember(memberId, { 
        skill_id: parseInt(skillId), 
        level_id: parseInt(targetLevelId),
        status: status || null
      });
      
      // Record this move in userMoves
      setUserMoves(prev => {
        // Find the latest move for this member-skill
        const memberMoves = prev.filter(move => 
          move.memberId === memberId && move.skillId === skillId
        );
        
        // Remove all previous moves for this member and skill
        const otherMoves = prev.filter(move => 
          move.memberId !== memberId || move.skillId !== skillId
        );
        
        // Add this move (with source and target)
        return [
          ...otherMoves,
          {
            memberId,
            skillId,
            sourceKey: sourceLevelId !== 'unassigned' ? `${sourceLevelId}-${skillId}` : null,
            targetKey: `${targetLevelId}-${skillId}`,
            timestamp: Date.now(),
            initialMove: memberMoves.length === 0 && isInitialAssignment
          }
        ];
      });
      
      // Notify parent about data change so other views can update
      if (onDataChange) {
        onDataChange();
      }
    } catch (error) {
      console.error('Error updating member skill level:', error);
    }
  }, [isAdmin, getMemberStatus, teamMembers, user?.email, onDataChange]);

  const handleDragStart = useCallback((event) => {
    setActiveDrag(event.active);
  }, []);

  // Create a unique droppable ID for each cell
  const getDroppableId = (skillId, levelId = 'unassigned') => {
    return `droppable-${skillId}-${levelId}`;
  };

  // Find the member being dragged
  const getDraggedMember = useCallback(() => {
    if (!activeDrag) return null;
    
    const [memberId] = activeDrag.id.split('-');
    return teamMembers.find(m => String(m.id) === memberId);
  }, [activeDrag, teamMembers]);

  return (
    <div className={`${isAdmin ? 'admin' : ''}`}>
      {/* Team member filter as chips (sticky, outside scroll wrapper) */}
      <ChipFilter 
        selectedMembers={selectedMembers}
        teamMembers={teamMembers}
        onChipClick={handleChipClick}
      />
      
      <div className="table-scroll-wrapper">
        {user ? (
          // Interactive view with drag and drop for both admins and regular users
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <table className={`table table-freeze-first-col ${isFiltered ? `filtered-table ${getFilteredCountClass()}` : ''}`}>
              {/* Table header rows */}
              <thead>
                <tr>
                  <th style={{ minWidth: 120 }}>Level \ Skill</th>
                  {grouped.map(group => (
                    <th key={group.id} colSpan={group.skills.length} style={{ 
                      textAlign: 'center', 
                      background: group.color || 'hsl(210, 50%, 95%)'
                    }}>
                      {group.title}
                    </th>
                  ))}
                </tr>
                <tr>
                  <th></th>
                  {grouped.map(group =>
                    group.skills.map(skill => (
                      <th key={skill.id} style={{ 
                        minWidth: 120,
                        background: group.color || 'hsl(210, 50%, 95%)' 
                      }}>{skill.title}</th>
                    ))
                  )}
                </tr>
              </thead>
              <tbody>
                {/* Unassigned row */}
                <tr>
                  <td style={{ fontWeight: 600 }}>0</td>
                  {grouped.map(group =>
                    group.skills.map(skill => {
                      const droppableId = getDroppableId(skill.id);
                      const skillUnassignedMembers = unassignedMembers[skill.id] || [];
                      const isValidTarget = activeDrag ? isValidDropTarget(activeDrag.id, droppableId) : false;
                      
                      return (
                        <td key={`unassigned-${skill.id}`} style={{ verticalAlign: 'top', position: 'relative' }}>
                          <DroppableArea 
                            id={droppableId}
                            isOver={activeDroppable === droppableId}
                            isValidDropTarget={(!activeDrag || isValidTarget)}
                          >
                            <SortableContext items={skillUnassignedMembers.map(m => `${m.id}-${skill.id}-unassigned`)} strategy={verticalListSortingStrategy}>
                              {skillUnassignedMembers.map(member => (
                                <TeamMemberEntry
                                  key={`${member.id}-${skill.id}-unassigned`}
                                  member={member}
                                  skillId={skill.id}
                                  levelId={null}
                                  status={null}
                                  onContextMenu={handleContextMenu}
                                  isAdmin={isAdmin}
                                />
                              ))}
                            </SortableContext>
                          </DroppableArea>
                        </td>
                      );
                    })
                  )}
                </tr>
                
                {/* Regular level rows */}
                {levels.map(level => (
                  <tr key={level.id}>
                    <td style={{ fontWeight: 600 }}>{level.title}</td>
                    {grouped.map(group =>
                      group.skills.map(skill => {
                        const cellKey = `${level.id}-${skill.id}`;
                        const selected = (cellSelections[cellKey] || [])
                          .filter(memberId => teamMembers.some(m => String(m.id) === memberId))
                          // Filter based on selectedMembers (chip filter)
                          .filter(memberId => 
                            selectedMembers.includes('all') || selectedMembers.includes(memberId)
                          );
                        const descObj = getDescription(skill.id, level.id);
                        const droppableId = getDroppableId(skill.id, level.id);
                        const isValidTarget = activeDrag ? isValidDropTarget(activeDrag.id, droppableId) : false;
                        
                        return (
                          <td key={cellKey} style={{ verticalAlign: 'top', position: 'relative' }}>
                            <DroppableArea 
                              id={droppableId}
                              isOver={activeDroppable === droppableId}
                              isValidDropTarget={(!activeDrag || isValidTarget)}
                            >
                              {/* Info button for description */}
                              {descObj && descObj.description && (
                                <span
                                  className="info-tooltip-trigger"
                                  onMouseEnter={e => setHoveredDesc({ key: cellKey, x: e.clientX, y: e.clientY, text: descObj.description })}
                                  onMouseLeave={() => setHoveredDesc(null)}
                                >
                                  <span className="info-icon">i</span>
                                </span>
                              )}
                              
                              {/* List selected members */}
                              <SortableContext items={selected.map(id => `${id}-${skill.id}-${level.id}`)} strategy={verticalListSortingStrategy}>
                                {selected.map(memberId => {
                                  const member = teamMembers.find(m => String(m.id) === memberId);
                                  if (!member) return null;
                                  
                                  // Get the status using our helper function
                                  const status = getMemberStatus(memberId, skill.id);
                                  
                                  return (
                                    <TeamMemberEntry
                                      key={`${memberId}-${skill.id}-${level.id}-${status || 'none'}`}
                                      member={member}
                                      skillId={skill.id}
                                      levelId={level.id}
                                      status={status}
                                      onContextMenu={handleContextMenu}
                                      isAdmin={isAdmin}
                                    />
                                  );
                                })}
                              </SortableContext>
                            </DroppableArea>
                          </td>
                        );
                      })
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Drag overlay */}
            <DragOverlay>
              {activeDrag && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: '#f8f9fa',
                  borderRadius: 4,
                  padding: '2px 6px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  opacity: 0.8
                }}>
                  {(() => {
                    const member = getDraggedMember();
                    if (!member) return null;
                    
                    return (
                      <>
                        {member.picture_url && (
                          <img
                            src={member.picture_url}
                            alt={member.name}
                            style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', marginRight: 6 }}
                          />
                        )}
                        <span>{member.name}</span>
                      </>
                    );
                  })()}
                </div>
              )}
            </DragOverlay>
          </DndContext>
        ) : (
          // Non-admin view without drag and drop
          <table className={`table table-freeze-first-col ${isFiltered ? `filtered-table ${getFilteredCountClass()}` : ''}`}>
            {/* Table header rows */}
            <thead>
              <tr>
                <th style={{ minWidth: 120 }}>Level \ Skill</th>
                {grouped.map(group => (
                  <th key={group.id} colSpan={group.skills.length} style={{ 
                    textAlign: 'center', 
                    background: group.color || 'hsl(210, 50%, 95%)'
                  }}>
                    {group.title}
                  </th>
                ))}
              </tr>
              <tr>
                <th></th>
                {grouped.map(group =>
                  group.skills.map(skill => (
                    <th key={skill.id} style={{ 
                      minWidth: 120,
                      background: group.color || 'hsl(210, 50%, 95%)' 
                    }}>{skill.title}</th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              {/* Unassigned row */}
              <tr>
                <td style={{ fontWeight: 600 }}>0</td>
                {grouped.map(group =>
                  group.skills.map(skill => {
                    const skillUnassignedMembers = unassignedMembers[skill.id] || [];
                    
                    return (
                      <td key={`unassigned-${skill.id}`} style={{ verticalAlign: 'top', position: 'relative' }}>
                        <div className="droppable-area">
                          {skillUnassignedMembers.map(member => (
                            <TeamMemberEntry
                              key={`${member.id}-${skill.id}-unassigned`}
                              member={member}
                              skillId={skill.id}
                              levelId={null}
                              status={null}
                              readOnly={true}
                              isAdmin={isAdmin}
                            />
                          ))}
                        </div>
                      </td>
                    );
                  })
                )}
              </tr>
              
              {/* Regular level rows for non-admin view */}
              {levels.map(level => (
                <tr key={level.id}>
                  <td style={{ fontWeight: 600 }}>{level.title}</td>
                  {grouped.map(group =>
                    group.skills.map(skill => {
                      const cellKey = `${level.id}-${skill.id}`;
                      const selected = (cellSelections[cellKey] || [])
                        .filter(memberId => teamMembers.some(m => String(m.id) === memberId))
                        // Filter based on selectedMembers (chip filter)
                        .filter(memberId => 
                          selectedMembers.includes('all') || selectedMembers.includes(memberId)
                        );
                      const descObj = getDescription(skill.id, level.id);
                      
                      return (
                        <td key={cellKey} style={{ verticalAlign: 'top', position: 'relative' }}>
                          <div className="droppable-area">
                            {/* Info button for description */}
                            {descObj && descObj.description && (
                              <span
                                className="info-tooltip-trigger"
                                onMouseEnter={e => setHoveredDesc({ key: cellKey, x: e.clientX, y: e.clientY, text: descObj.description })}
                                onMouseLeave={() => setHoveredDesc(null)}
                              >
                                <span className="info-icon">i</span>
                              </span>
                            )}
                            
                            {/* List selected members */}
                            {selected.map(memberId => {
                              const member = teamMembers.find(m => String(m.id) === memberId);
                              if (!member) return null;
                              
                              // Get the status using our helper function
                              const status = getMemberStatus(memberId, skill.id);
                              
                              return (
                                <TeamMemberEntry
                                  key={`${memberId}-${skill.id}-${level.id}-${status || 'none'}`}
                                  member={member}
                                  skillId={skill.id}
                                  levelId={level.id}
                                  status={status}
                                  readOnly={true}
                                  isAdmin={isAdmin}
                                />
                              );
                            })}
                          </div>
                        </td>
                      );
                    })
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      {/* Tooltip for description */}
      {hoveredDesc && (
        <Tooltip
          x={hoveredDesc.x}
          y={hoveredDesc.y}
          text={hoveredDesc.text}
        />
      )}
      
      {/* Context menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          options={[
            {
              label: 'Set as Focus',
              icon: '🌱',
              onClick: () => {
                handleUpdateStatus(contextMenu.memberId, contextMenu.skillId, 'focus');
              },
              selected: contextMenu.status === 'focus'
            },
            // Only show Watched option for admins
            ...(isAdmin ? [{
              label: 'Set to Observe',
              icon: '👁️',
              onClick: () => {
                handleUpdateStatus(contextMenu.memberId, contextMenu.skillId, 'watched');
              },
              selected: contextMenu.status === 'watched'
            }] : []),
            {
              label: 'Clear Status',
              icon: '❌',
              onClick: () => {
                handleUpdateStatus(contextMenu.memberId, contextMenu.skillId, 'none');
              },
              selected: contextMenu.status === 'none'
            }
          ]}
        />
      )}
    </div>
  );
}

export default TableView; 